import _ from 'lodash';
import type { MouseEvent, ReactElement } from 'react';
import { memo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { HighlightText, Tag, Tooltip, useInViewport, useStaggeredMount } from '../../components';
import { Store } from '../../store';
import type { CardSizeLevel } from './card-size';
import { cardActionFontSize, cardTitleFontSize, DEFAULT_CARD_SIZE } from './card-size';
import { EventCard } from './components';
import { Countdown } from './countdown';
import { Destinations } from './destinations';
import { eventTypeInfo, formatTime, STATUS, stopNav, toEventView } from './event-model';
import type { ScheduledEvent, UntitledEvent } from './graphql';

interface Props {
  event: ScheduledEvent | UntitledEvent;
  search?: string;
  size?: CardSizeLevel;
  enableFavorite?: boolean;
  showVideo?: boolean;
  isFavorite?: boolean;
  initialVolume?: number;
  /*
   * Both hand the event id back rather than closing over it. That lets the grid pass one callback
   * to every card instead of minting a fresh arrow per card on every render, which would defeat
   * the memo below before it had a chance to help.
   */
  onToggleFavorite?: (eid: string) => void;
  onVolumeChange?: (eid: string, volume: number) => void;
}

const Container = styled(Link)<{ $highPriority: boolean }>`
  /* Keep the card's absolutely-positioned tags (z-index) inside this card so
     they can't paint over the fixed header/menu (a higher-level layer). */
  isolation: isolate;
  display: flex;
  flex-direction: column;
  gap: 11px;
  padding: 13px;
  border-radius: 12px;
  background: rgba(17, 27, 51, 0.5);
  /*
   * 1px ring painted as an inset shadow instead of a real border: the grid's
   * auto-fill 1fr tracks give cards sub-pixel widths, and a 1px border
   * rasterizes unevenly on those fractional edges (very visible with the red
   * highlight). An inset box-shadow stays crisp and doesn't alter the box model.
   */
  box-shadow: inset 0 0 0 1px
    ${({ $highPriority }): string => ($highPriority ? 'rgb(255, 80, 95)' : 'rgba(255, 255, 255, 0.08)')};
  color: var(--white);
  text-decoration: none;
  transition: all var(--transition);

  &:hover,
  &:focus {
    color: var(--white);
    text-decoration: none;
    box-shadow: inset 0 0 0 1px
      ${({ $highPriority }): string =>
        $highPriority ? 'rgb(255, 80, 95)' : 'rgba(255, 255, 255, 0.18)'};
  }
`;

const Header = styled.div<{ $size: CardSizeLevel }>`
  display: flex;
  align-items: center;
  gap: 10px;

  /* Mulish (the app default), not the mono face — inherits from body on purpose. */
  .ids {
    display: flex;
    align-items: center;
    gap: 5px;
    flex-grow: 1;
    min-width: 0;
    font-size: 12px;
    color: var(--white);
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .action {
    display: inline-flex;
    padding: 0;
    background: transparent;
    border: none;
    color: var(--white60);
    font-size: ${({ $size }): string => cardActionFontSize($size)};

    &:hover {
      color: var(--white);
    }
  }
`;

const Thumb = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  border-radius: 8px;
  overflow: hidden;
  background: #0b1226;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .placeholder {
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;
    color: var(--white35);
    font-size: 22px;
  }

  .video {
    position: absolute;
    inset: 0;
    z-index: 1;
  }

  /* Fill the 16:9 thumb, cropping the sides instead of letterboxing the stream. */
  .video video {
    object-fit: cover !important;
  }

  .status {
    position: absolute;
    z-index: 2;
    top: 8px;
    left: 8px;
  }
`;

const LiveBadge = styled.div`
  position: absolute;
  z-index: 2;
  top: 9px;
  left: 9px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(0, 16, 45, 0.7);
  color: rgb(255, 255, 255);
  font-weight:700;
  font-size: 10px;
  letter-spacing: 0.08em;
  padding: 4px 9px;
  border-radius: 999px;

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgb(53, 211, 153);
  }
`;

const UpcomingBadge = styled(LiveBadge)`
  .dot {
    background: rgb(120, 130, 255);
  }
`;

const Title = styled.h3<{ $size: CardSizeLevel }>`
  margin: 0;
  color: var(--white) !important;
  font-size: ${({ $size }): string => cardTitleFontSize($size)};
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Time = styled.p`
  margin: 0;
  font-size: 12px;
  color: var(--white60);
`;

const Stats = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: var(--white60);

  .tags {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .destinations {
    display: flex;
    align-items: center;
  }
`;

/*
 * Memoised because the grid keeps every card it has ever loaded mounted — infinite scroll, no
 * virtualisation — so any state change in the page used to re-render all of them. Its props are
 * primitives plus the event object, which Apollo keeps referentially stable while it is unchanged.
 */
const CardV2Component = ({
  event,
  search,
  size = DEFAULT_CARD_SIZE,
  enableFavorite = false,
  showVideo = false,
  isFavorite = false,
  initialVolume,
  onToggleFavorite,
  onVolumeChange
}: Props): ReactElement => {
  const view = toEventView(event);
  const badge = STATUS[view.status];
  const type = eventTypeInfo(view.scheduled?.eventType);

  const { me } = Store.User.useIdentity();

  /*
   * The star reflects two independent things: being the event's validator (locked
   * on, can't unfavorite) or having favorited it. Validator takes precedence.
   */
  // GraphQL can return null for these (typed loosely), so guard before comparing.
  const myEmail = me?.email.toLowerCase() ?? '';
  const isValidator = myEmail !== '' && view.verifierEmail?.toLowerCase() === myEmail;

  const starred = isValidator || isFavorite;

  let starTooltip = 'Add to favorites';

  if (isValidator) {
    starTooltip = 'You are the validator for this event';
  } else if (isFavorite) {
    starTooltip = 'Remove from favorites';
  }

  const onStarClick = (clickEvent: MouseEvent<HTMLButtonElement>): void => {
    // Drop focus so the tooltip doesn't linger via :focus-within after a click.
    clickEvent.currentTarget.blur();

    // Validators can't toggle; otherwise this adds (unmarked) or removes (favorite).
    if (isValidator) {
      return;
    }

    onToggleFavorite?.(view.eid);
  };

  const notesTooltip = view.supportNotes ? 'Operator notes' : 'No operator notes';
  const checkinTooltip = view.checkedUser ? `Checked-in by: ${view.checkedUser}` : 'Not checked in';

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleVolumeChange = useCallback(
    _.debounce((volume: number): void => {
      onVolumeChange?.(view.eid, volume);
    }, 500),
    [onVolumeChange, view.eid]
  );

  // Only live events auto-play; upcoming/past never do (even if a stream URL exists).
  const videoSrc = showVideo && view.status === 'live' ? view.muxStreamUrl : undefined;

  /*
   * The player is mounted only while the card is on screen. The list doesn't
   * virtualize, so every page you scroll past stays in the DOM — without this,
   * each live card keeps an HLS stream buffering and decoding forever.
   */
  const [thumbRef, thumbVisible] = useInViewport<HTMLDivElement>();

  /*
   * On screen and live is what makes a player wanted; the queue decides when it actually mounts.
   * Without it, a batch of events going live together built every visible player in one frame.
   */
  const wantsVideo = videoSrc !== undefined && thumbVisible;

  /* The source once its turn comes, and undefined until then — the branch below keys off it. */
  const playingSrc = useStaggeredMount(wantsVideo) ? videoSrc : undefined;

  /*
   * The preview navigates like the rest of the card. The player's own controls are
   * exempt: adjusting the volume, or hitting play when autoplay was blocked, should
   * do just that. Everything else inside the video bubbles up to the card's Link.
   */
  const PLAYER_CONTROLS = '.vjs-control-bar, .vjs-big-play-button';

  const onVideoClick = (clickEvent: MouseEvent<HTMLDivElement>): void => {
    if (clickEvent.target instanceof Element && clickEvent.target.closest(PLAYER_CONTROLS)) {
      stopNav(clickEvent);
    }
  };

  return (
    <Container to={view.to} $highPriority={view.highPriority}>
      <Header $size={size}>
        <div className='ids'>
          {view.cid && (
            <div>
              CID: <HighlightText highlight={search}>{view.cid}</HighlightText>
            </div>
          )}
          <div>
            EID: <HighlightText highlight={search}>{view.eid}</HighlightText>
          </div>
        </div>

        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div className='actions' onClick={stopNav}>
          <Tooltip content={notesTooltip} placement='bottom-right'>
            <button type='button' className='action' style={{ cursor: 'default' }}>
              <i
                className={view.supportNotes ? 'fas fa-clipboard' : 'far fa-clipboard'}
                style={{ color: view.supportNotes ? '#ffd27a' : undefined }}
              />
            </button>
          </Tooltip>

          <Tooltip content={checkinTooltip} placement='bottom-right'>
            <button type='button' className='action' style={{ cursor: 'default' }}>
              <i
                className={view.checkedUser ? 'fas fa-thumbs-up' : 'far fa-thumbs-up'}
                style={{ color: view.checkedUser ? '#7fe6b8' : undefined }}
              />
            </button>
          </Tooltip>

          {enableFavorite && (
            <Tooltip content={starTooltip} placement='bottom-right' maxWidth={190}>
              <button
                type='button'
                className='action'
                style={{ cursor: isValidator ? 'not-allowed' : 'pointer' }}
                onClick={onStarClick}
              >
                <i
                  className={starred ? 'fas fa-star' : 'far fa-star'}
                  style={{ color: starred ? '#ffd27a' : undefined }}
                />
              </button>
            </Tooltip>
          )}
        </div>
      </Header>

      <Thumb ref={thumbRef}>
        {playingSrc !== undefined ? (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div className='video' onClick={onVideoClick}>
            <EventCard.C.Video
              src={playingSrc}
              initialVolume={initialVolume}
              onVolumeChange={handleVolumeChange}
              muted
            />
          </div>
        ) : view.thumbnail ? (
          <img src={view.thumbnail} alt={view.title} />
        ) : (
          <div className='placeholder'>
            <i className='far fa-image' />
          </div>
        )}
        {view.status === 'live' && (
          <LiveBadge>
            <span className='dot cc-blink-slow' />
            {badge.label}
          </LiveBadge>
        )}
        {view.status === 'past' && (
          <Tag size='small' className={`status ${badge.className}`} icon={badge.icon}>
            {badge.label}
          </Tag>
        )}
        {view.status === 'upcoming' && (
          <>
            <UpcomingBadge>
              <span className='dot' />
              {badge.label}
            </UpcomingBadge>
            <Countdown start={view.start} />
          </>
        )}
      </Thumb>

      <Title $size={size}>
        <HighlightText highlight={search}>
          {view.organizationName ? `${view.organizationName} — ${view.title}` : view.title}
        </HighlightText>
      </Title>

      <Time>{formatTime(view.start, view.end)}</Time>

      {view.scheduled && (
        <Stats>
          <div className='tags'>
            <Tag size='small'>{type.label}</Tag>
            {view.isFirstEvent && (
              <Tag size='small' className='cc-amber'>
                First Event
              </Tag>
            )}
            {view.receiveIsoRecordings && (
              <Tag size='small' className='cc-neutral'>
                ISO
              </Tag>
            )}
            {view.hasAudioMix && (
              <Tag size='small' className='cc-neutral'>
                Audio Mix
              </Tag>
            )}
            {view.hasVideographer && (
              <Tag size='small' className='cc-neutral'>
                DVG
              </Tag>
            )}
          </div>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div className='destinations' onClick={stopNav}>
            <Destinations {...view.scheduled} />
          </div>
        </Stats>
      )}
    </Container>
  );
};

export const CardV2 = memo(CardV2Component);
